import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, IconButton, Chip, Stack, CircularProgress, Alert, Tooltip,
  InputAdornment, FormControlLabel, Switch
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { lookupErrorText } from '../../utils/lookupError';
import ApproximateMatchNotice from '../ApproximateMatchNotice';
import VariableStarInfo from '../VariableStarInfo';
import { MOON_PEAK_DEFAULT, MOON_WIDTH_DEFAULT } from '../../constants/moon';

const TARGET_TYPES = [
  { value: 'deep_sky', label: 'Deep Sky' },
  { value: 'exoplanet', label: 'Exoplanet' },
  { value: 'variable_star', label: 'Variable Star' },
  { value: 'asteroid', label: 'Asteroid' },
  { value: 'custom', label: 'Custom' },
];

const TargetForm = ({ open, onClose, onSave, target, projects, observatories, organizations }) => {
  const isEdit = !!target;

  const [form, setForm] = useState(() => ({
    name: target?.name || '',
    project_id: target?.project_id?._id || target?.project_id || '',
    target_type: target?.target_type || 'deep_sky',
    ra: target?.ra?.toString() || '',
    dec: target?.dec?.toString() || '',
    epoch: target?.epoch || 'J2000',
    rotation: target?.rotation || 0,
    catalog_id: target?.catalog_id || '',
    notes: target?.notes || '',
    tags: target?.tags || [],
    visibility: target?.visibility || 'private',
    observatory_id: target?.observatory_id?._id || target?.observatory_id || '',
    organization_id: target?.organization_id?._id || target?.organization_id || '',
    constraints: {
      minimum_altitude: target?.constraints?.minimum_altitude ?? 30,
      maximum_airmass: target?.constraints?.maximum_airmass ?? 2.0,
      // #1164: moon_separation is the requirement AT FULL MOON (Lorentzian peak); width is the
      // half-width in days; moon_down requires the moon below the horizon. Before #1164 this form
      // could only set the peak, so width/moon_down were lost on save.
      moon_separation: target?.constraints?.moon_separation ?? MOON_PEAK_DEFAULT,
      moon_avoidance_width: target?.constraints?.moon_avoidance_width ?? MOON_WIDTH_DEFAULT,
      moon_down: target?.constraints?.moon_down ?? false,
      twilight_type: target?.constraints?.twilight_type || 'nautical',
    },
  }));

  const [tagInput, setTagInput] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  const handleLookup = async () => {
    if (!form.name) return;
    setLookingUp(true);
    setLookupResult(null);
    setLookupError(null);
    try {
      const response = await apiService.lookupCoordinates(form.name);
      if (response.success && response.data) {
        const result = response.data;
        setForm(prev => ({
          ...prev,
          ra: result.coordinates.ra.toFixed(6),
          dec: result.coordinates.dec.toFixed(6),
          catalog_id: result.resolvedName || prev.catalog_id,
        }));
        setLookupResult(result);
      } else {
        setLookupError('Target not found');
      }
    } catch (error) {
      setLookupError(lookupErrorText(error, 'Coordinate lookup failed'));
    } finally {
      setLookingUp(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (group, field, value) => {
    setForm(prev => ({
      ...prev,
      [group]: { ...prev[group], [field]: value }
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      handleChange('tags', [...form.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    handleChange('tags', form.tags.filter(t => t !== tag));
  };

  const handleSubmit = () => {
    const data = {
      ...form,
      ra: parseFloat(form.ra),
      dec: parseFloat(form.dec),
      rotation: parseFloat(form.rotation) || 0,
      visibility: form.visibility || 'private',
      observatory_id: form.visibility === 'observatory' ? form.observatory_id : undefined,
      organization_id: form.visibility === 'organization' ? form.organization_id : undefined,
      constraints: {
        ...form.constraints,
        minimum_altitude: parseFloat(form.constraints.minimum_altitude),
        maximum_airmass: parseFloat(form.constraints.maximum_airmass),
        moon_separation: parseFloat(form.constraints.moon_separation),
        moon_avoidance_width: parseFloat(form.constraints.moon_avoidance_width),
        moon_down: !!form.constraints.moon_down,
      },
    };
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Library Target' : 'Add Library Target'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Basic info */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Target Name" fullWidth required
              value={form.name} onChange={e => handleChange('name', e.target.value)}
              helperText="Enter name and click search to look up coordinates"
              onKeyDown={e => { if (e.key === 'Enter' && form.name) { e.preventDefault(); handleLookup(); } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Look up coordinates">
                      <span>
                        <IconButton
                          onClick={handleLookup}
                          disabled={!form.name || lookingUp}
                          edge="end"
                        >
                          {lookingUp ? <CircularProgress size={20} /> : <SearchIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Project</InputLabel>
              <Select value={form.project_id} onChange={e => handleChange('project_id', e.target.value)} label="Project">
                {(projects || []).map(p => (
                  <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Visibility */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sharing</InputLabel>
              <Select
                value={form.visibility}
                onChange={e => {
                  const v = e.target.value;
                  handleChange('visibility', v);
                  if (v === 'private') { handleChange('observatory_id', ''); handleChange('organization_id', ''); }
                  if (v === 'observatory') { handleChange('organization_id', ''); }
                  if (v === 'organization') { handleChange('observatory_id', ''); }
                }}
                label="Sharing"
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="observatory">Observatory</MenuItem>
                <MenuItem value="organization">Organization</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {form.visibility === 'observatory' && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Observatory</InputLabel>
                <Select
                  value={form.observatory_id}
                  onChange={e => handleChange('observatory_id', e.target.value)}
                  label="Observatory"
                >
                  {(observatories || []).map(o => (
                    <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {form.visibility === 'organization' && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={form.organization_id}
                  onChange={e => handleChange('organization_id', e.target.value)}
                  label="Organization"
                >
                  {(organizations || []).map(o => (
                    <MenuItem key={o._id || o.organization_id} value={o._id || o.organization_id}>
                      {o.name || o.organization_name || 'Unknown'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {lookupError && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setLookupError(null)}>
                {lookupError}
              </Alert>
            </Grid>
          )}
          {lookupResult && (
            <Grid item xs={12}>
              <ApproximateMatchNotice result={lookupResult} query={form.name} sx={{ mb: lookupResult.approximateMatch ? 1 : 0 }} />
              <Alert severity={lookupResult.approximateMatch ? 'info' : 'success'} onClose={() => setLookupResult(null)}>
                Resolved via {lookupResult.service}: <strong>{lookupResult.resolvedName}</strong>
                {' '}&mdash; RA: {lookupResult.coordinates.ra.toFixed(4)}°, Dec: {lookupResult.coordinates.dec.toFixed(4)}°
                <VariableStarInfo variableStar={lookupResult.coordinates.variableStar} sx={{ mt: 0.5 }} />
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={form.target_type} onChange={e => handleChange('target_type', e.target.value)} label="Type">
                {TARGET_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Coordinates */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="RA (degrees)" fullWidth required type="number"
              value={form.ra} onChange={e => handleChange('ra', e.target.value)}
              inputProps={{ step: 0.0001, min: 0, max: 360 }}
              helperText="0-360 decimal degrees"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Dec (degrees)" fullWidth required type="number"
              value={form.dec} onChange={e => handleChange('dec', e.target.value)}
              inputProps={{ step: 0.0001, min: -90, max: 90 }}
              helperText="-90 to +90 decimal degrees"
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField label="Epoch" fullWidth value={form.epoch} onChange={e => handleChange('epoch', e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Rotation" fullWidth type="number"
              value={form.rotation} onChange={e => handleChange('rotation', e.target.value)}
              inputProps={{ min: 0, max: 360 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Catalog ID" fullWidth
              value={form.catalog_id} onChange={e => handleChange('catalog_id', e.target.value)}
              helperText="SIMBAD / catalog reference"
            />
          </Grid>

          {/* Constraints */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Constraints</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Min Altitude" fullWidth type="number" size="small"
              value={form.constraints.minimum_altitude}
              onChange={e => handleNestedChange('constraints', 'minimum_altitude', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Max Airmass" fullWidth type="number" size="small"
              value={form.constraints.maximum_airmass}
              onChange={e => handleNestedChange('constraints', 'maximum_airmass', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Tooltip title="Separation required at full moon. The requirement eases toward new moon (Lorentzian model).">
              <TextField
                label="Full Moon Sep." fullWidth type="number" size="small"
                value={form.constraints.moon_separation}
                onChange={e => handleNestedChange('constraints', 'moon_separation', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Tooltip title="How many days either side of full moon the requirement stays high (Lorentzian half-width, 1-10).">
              <TextField
                label="Moon Width" fullWidth type="number" size="small"
                value={form.constraints.moon_avoidance_width}
                onChange={e => handleNestedChange('constraints', 'moon_avoidance_width', e.target.value)}
                InputProps={{ inputProps: { min: 1, max: 10, step: 1 }, endAdornment: <InputAdornment position="end">d</InputAdornment> }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.constraints.moon_down}
                  onChange={e => handleNestedChange('constraints', 'moon_down', e.target.checked)}
                />
              }
              label="Moon down"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Twilight</InputLabel>
              <Select
                value={form.constraints.twilight_type}
                onChange={e => handleNestedChange('constraints', 'twilight_type', e.target.value)}
                label="Twilight"
              >
                <MenuItem value="civil">Civil</MenuItem>
                <MenuItem value="nautical">Nautical</MenuItem>
                <MenuItem value="astronomical">Astronomical</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Tags</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <TextField
                size="small" label="Add tag" value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              />
              <Button size="small" onClick={addTag}>Add</Button>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {form.tags.map(tag => (
                <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)} />
              ))}
            </Stack>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              label="Notes" fullWidth multiline rows={2}
              value={form.notes} onChange={e => handleChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!form.name || !form.ra || !form.dec || !form.project_id}>
          {isEdit ? 'Save Changes' : 'Add Target'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TargetForm;
